import { useEffect } from 'react';

/**
 * 모달이 열려 있는 동안 배경(body) 스크롤·터치를 잠근다.
 * 원본: naegift-user-app/src/hooks/useBodyScrollLock.ts (타입 표기만 제거)
 *
 * ★react-modal 이 붙여주는 bodyOpenClassName('ReactModal__Body--open') 은 클래스만 달아줄 뿐
 *   이 프로젝트에는 대응 CSS 가 없어 실제로 잠기지 않는다(전역 CSS 어디에도 규칙 없음).
 *   그래서 클래스에 기대지 않고 여기서 직접 잠근다.
 * ★overflow:hidden 만으로는 iOS Safari/WKWebView 에서 배경 스크롤이 새므로 touchmove 차단을 더한다.
 *
 * @param locked         잠글지 여부(팝업 열림 상태)
 * @param allowScrollRef 잠금 중에도 스크롤을 허용할 요소(팝업 본문). 이 안에서 일어난
 *                       touchmove 는 막지 않아 긴 공지 본문을 계속 스크롤할 수 있다.
 */

/**
 * 참조 카운트 — 잠금이 겹쳐도(예: 팝업 위에 다른 모달) 먼저 푼 쪽이 배경을 되살리지 않게 한다.
 * 첫 잠금만 원래 값을 저장하고, 마지막 해제만 복원한다.
 */
let lockCount = 0;
let savedBodyOverflow = '';
let savedHtmlOverflow = '';

export default function useBodyScrollLock(locked, allowScrollRef) {
  useEffect(() => {
    if (!locked) return undefined;

    lockCount += 1;
    if (lockCount === 1) {
      savedBodyOverflow = document.body.style.overflow;
      savedHtmlOverflow = document.documentElement.style.overflow;
      document.body.style.overflow = 'hidden';
      // html 까지 막는 이유: 일부 모바일 브라우저는 스크롤 주체가 body 가 아니라 documentElement 다.
      document.documentElement.style.overflow = 'hidden';
    }

    /**
     * 터치 스크롤 차단.
     * ★touch-action:none 을 오버레이에 주는 방식은 쓰지 않는다 — touch-action 은 조상에서 none 이면
     *   후손도 팬(pan)이 불가능해져 팝업 본문 내부 스크롤까지 같이 죽는다.
     *   대신 문서 레벨 touchmove 를 막되, 허용 요소(본문) 안에서 난 이벤트만 통과시킨다.
     * ★본문 끝에서 배경으로 이어지는 스크롤 체이닝은 CSS 쪽 overscroll-behavior:contain
     *   (NoticePopup.module.css .body)이 막는다.
     */
    const onTouchMove = e => {
      const scroller = allowScrollRef?.current;
      const target = e.target;
      if (scroller && target && scroller.contains(target)) return;
      // passive:false 로 등록해야 preventDefault 가 먹는다(모바일 기본은 passive).
      if (e.cancelable) e.preventDefault();
    };
    document.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      document.removeEventListener('touchmove', onTouchMove);
      lockCount -= 1;
      if (lockCount === 0) {
        document.body.style.overflow = savedBodyOverflow;
        document.documentElement.style.overflow = savedHtmlOverflow;
      }
    };
  }, [locked, allowScrollRef]);
}
